// @djsem/analysis-contract — AI Cue Director / SKIP-BERT Hugging Face Cue Intelligence (Prompt 97).
//
// Provider-agnostic AI lighting/visual CUE SUGGESTION engine. This layer NEVER executes a real
// model and NEVER drives hardware — it validates model config, normalizes suggestions, gates
// them through the safety boundary, and maps APPROVED suggestions to dry-run / semantic /
// approved-cue events. Fails closed: no fake suggestions on failure, no export of unapproved
// cues, blackout + safe-mode always win.
//
// The Hugging Face model id is NOT hardcoded. A model must be configured + validated (license,
// task type, tokenizer, runtime, commercial-use) before it can be enabled for production. Until
// then the disabled/stub provider is used, which produces NO suggestions outside test fixtures.

import { type DJSEMValidationResult, toDJSEMValidationResult, type ValidationResult, SeqCounter } from "@djsem/shared";

const AT = "1970-01-01T00:00:00.000Z";
const _seq = new SeqCounter();
function id(prefix: string): string { return `${prefix}_${_seq.next()}`; }

// ---- suggestion + intent taxonomies (from the prompt) -----------------------

/** The category of an AI cue suggestion (lighting scene change, drop cue, lyric hit, etc). */
export type DJSEMAICueSuggestionType =
  | "lighting_scene" | "lighting_transition" | "visual_scene" | "drop_cue" | "build_cue"
  | "breakdown_cue" | "blackout_suggestion" | "freeze_suggestion" | "strobe_suggestion"
  | "colour_palette_shift" | "intensity_ramp" | "lyric_hit" | "stem_onset" | "manual_operator_note";

/** The emotional/energy intent a lighting cue suggestion is meant to convey. */
export type DJSEMLightingCueIntent =
  | "warmup" | "tension" | "build" | "release" | "impact" | "drop" | "breakdown" | "uplift"
  | "darken" | "pulse" | "freeze" | "blackout" | "crowd_moment" | "lyric_emphasis"
  | "transition_cover" | "safe_idle";

/** Suggestion types that always require extra human confirmation before they can be exported. */
export const SAFETY_SENSITIVE_CUE_TYPES: ReadonlySet<DJSEMAICueSuggestionType> = new Set<DJSEMAICueSuggestionType>([
  "blackout_suggestion", "strobe_suggestion", "freeze_suggestion",
]);

/** Human-review lifecycle status of a single AI cue suggestion. */
export type DJSEMCueApprovalStatus = "suggested" | "approved" | "rejected" | "edited" | "exported" | "archived";

// ---- contracts (verbatim shape from the prompt) -----------------------------

/** Configuration for a Hugging Face model backing the AI Cue Director. Must be validated (license, commercial-use) before production use. */
export interface DJSEMHuggingFaceCueModelConfig {
  id: string;
  providerId: "huggingface";
  modelId: string;
  revision?: string;
  tokenizerId?: string;
  taskType: "text_classification" | "sequence_classification" | "token_classification" | "text2text_generation" | "custom";
  runtime: "python_local" | "node_onnx" | "remote_inference_api" | "disabled";
  maxInputTokens?: number;
  batchSize?: number;
  timeoutMs: number;
  commercialUseAllowed?: boolean;
  license?: string;
  modelCardUrl?: string;
  warnings: string[];
}

/** A request to generate AI lighting/visual cue suggestions from track/set/lyric/stem inputs. */
export interface DJSEMAICueSuggestionRequest {
  id: string;
  organisationId?: string;
  projectId?: string;
  trackId?: string;
  setId?: string;
  showPackId?: string;
  sourceType: "track" | "set" | "chapter" | "phrase" | "lyrics" | "stem_activity" | "rehearsal" | "live_dry_run";
  inputs: {
    metadata?: Record<string, unknown>;
    lyricsText?: string;
    semanticProfile?: Record<string, unknown>;
    chapterTimeline?: unknown[];
    phraseTimeline?: unknown[];
    stemActivity?: unknown[];
    transitionPlan?: unknown;
    eventBrief?: unknown;
    venueProfile?: unknown;
    brandWorld?: unknown;
    lightingSceneLibrary?: unknown[];
  };
  requestedCueTypes: DJSEMAICueSuggestionType[];
  safetyMode: "suggest_only" | "rehearsal_dry_run" | "approval_required";
  createdByUserId?: string;
  createdAt: string;
}

/** A single suggested lighting/visual cue, with timing, confidence, and approval status. */
export interface DJSEMAICueSuggestion {
  id: string;
  requestId: string;
  trackId?: string;
  setId?: string;
  showPackId?: string;
  cueType: DJSEMAICueSuggestionType;
  intent: DJSEMLightingCueIntent;
  label: string;
  description: string;
  startTimeMs?: number;
  endTimeMs?: number;
  bar?: number;
  beat?: number;
  phraseId?: string;
  chapterId?: string;
  semanticEventId?: string;
  suggestedIntensity: number;
  suggestedColorPalette?: string[];
  suggestedSceneId?: string;
  suggestedAdapterMappingId?: string;
  confidence: number;
  modelId: string;
  modelRevision?: string;
  modelRawOutputRef?: string;
  approvalStatus: DJSEMCueApprovalStatus;
  safetyWarnings: string[];
  rightsWarnings: string[];
  createdAt: string;
  updatedAt: string;
}

/** The result of running a cue-suggestion request: accepted + rejected suggestions and warnings. */
export interface DJSEMAICueSuggestionReport {
  id: string;
  requestId: string;
  status: "completed" | "completed_with_warnings" | "failed";
  modelId: string;
  modelRevision?: string;
  inputSummary: { trackCount?: number; chapterCount?: number; lyricLineCount?: number; stemCount?: number };
  suggestions: DJSEMAICueSuggestion[];
  rejectedSuggestions: DJSEMAICueSuggestion[];
  warnings: string[];
  errors: string[];
  createdAt: string;
}

// ---- model adapter ----------------------------------------------------------

/** Runtime backing a cue model provider (local process, ONNX, remote API, stub, or disabled). */
export type DJSEMCueProviderType = "python_local" | "node_onnx" | "remote_inference_api" | "manual_stub" | "disabled";

/** Describes what a cue model provider supports (task types, cue types, production-readiness). */
export interface DJSEMCueModelCapabilities {
  providerId: string;
  providerType: DJSEMCueProviderType;
  label: string;
  supportedTaskTypes: DJSEMHuggingFaceCueModelConfig["taskType"][];
  supportedCueTypes: DJSEMAICueSuggestionType[];
  productionEnabled: boolean;
  warnings: string[];
}

/**
 * Hugging Face cue model adapter. Validates config and normalizes output. The base adapter does
 * NOT run a real model (that requires a verified model card + runtime). Concrete runtimes extend
 * runInference; the manual-stub/disabled providers below produce no suggestions outside tests.
 */
export class DJSEMHuggingFaceCueModelAdapter {
  /**
   * Validate a model config against the non-negotiable rules: a model id is required, an unknown
   * or non-commercial license blocks production, "disabled" runtime is always allowed (safe),
   * and a missing model card is a warning (can't verify). Never throws.
   */
  validateModelConfig(config: DJSEMHuggingFaceCueModelConfig): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (config.providerId !== "huggingface") issues.push({ path: "$.providerId", message: "providerId must be 'huggingface'." });
    if (!config.modelId || !config.modelId.trim()) issues.push({ path: "$.modelId", message: "A Hugging Face modelId is required (do not leave blank)." });
    if (!Number.isFinite(config.timeoutMs) || config.timeoutMs <= 0) issues.push({ path: "$.timeoutMs", message: "timeoutMs must be a positive number." });
    // License / commercial-use gating: production requires a known, commercial-use-allowed license.
    if (config.runtime !== "disabled") {
      if (!config.license || !config.license.trim()) issues.push({ path: "$.license", message: "License is unknown — production use is blocked until the model card license is verified." });
      else if (config.commercialUseAllowed !== true) issues.push({ path: "$.commercialUseAllowed", message: "Commercial use is not confirmed for this license — production use is blocked." });
      if (!config.modelCardUrl) issues.push({ path: "$.modelCardUrl", message: "modelCardUrl missing — cannot verify model card; provide it before enabling." });
    }
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "huggingface_cue_model_config");
  }

  /** Whether a config is safe to enable for PRODUCTION inference (not just structurally valid). */
  isProductionEnableable(config: DJSEMHuggingFaceCueModelConfig): boolean {
    return this.validateModelConfig(config).ok && config.runtime !== "disabled";
  }

  /** Prepare a compact, model-neutral input payload from the request (no model call). */
  prepareInput(request: DJSEMAICueSuggestionRequest): {
    sourceType: string; requestedCueTypes: DJSEMAICueSuggestionType[]; hasLyrics: boolean;
    chapterCount: number; phraseCount: number; stemCount: number;
  } {
    return {
      sourceType: request.sourceType,
      requestedCueTypes: request.requestedCueTypes,
      hasLyrics: !!request.inputs.lyricsText && request.inputs.lyricsText.trim().length > 0,
      chapterCount: request.inputs.chapterTimeline?.length ?? 0,
      phraseCount: request.inputs.phraseTimeline?.length ?? 0,
      stemCount: request.inputs.stemActivity?.length ?? 0,
    };
  }

  /**
   * Base runtime does NOT fabricate suggestions — a real model must be wired in a verified
   * runtime. Returns an empty raw output so downstream normalization yields no suggestions.
   */
  async runInference(_input: unknown): Promise<{ raw: unknown[] }> {
    return { raw: [] };
  }

  /**
   * Normalize raw model output into DJSEMAICueSuggestion objects. Only accepts entries that carry
   * the required fields; anything malformed is dropped (fail closed). Confidence is clamped 0..1.
   */
  async normalizeOutput(rawOutput: { raw: unknown[] }, request: DJSEMAICueSuggestionRequest, modelId: string, modelRevision?: string): Promise<DJSEMAICueSuggestion[]> {
    const out: DJSEMAICueSuggestion[] = [];
    for (const entry of rawOutput.raw ?? []) {
      const e = entry as Partial<DJSEMAICueSuggestion> & { cueType?: string; intent?: string };
      if (!e || typeof e.cueType !== "string" || typeof e.intent !== "string") continue;
      out.push({
        id: id("cue"), requestId: request.id, trackId: request.trackId, setId: request.setId, showPackId: request.showPackId,
        cueType: e.cueType as DJSEMAICueSuggestionType, intent: e.intent as DJSEMLightingCueIntent,
        label: e.label ?? `${e.cueType}`, description: e.description ?? "",
        startTimeMs: e.startTimeMs, endTimeMs: e.endTimeMs, bar: e.bar, beat: e.beat,
        phraseId: e.phraseId, chapterId: e.chapterId, semanticEventId: e.semanticEventId,
        suggestedIntensity: clamp01(e.suggestedIntensity ?? 0.5), suggestedColorPalette: e.suggestedColorPalette,
        suggestedSceneId: e.suggestedSceneId, suggestedAdapterMappingId: e.suggestedAdapterMappingId,
        confidence: clamp01(e.confidence ?? 0), modelId, modelRevision, modelRawOutputRef: e.modelRawOutputRef,
        approvalStatus: "suggested", safetyWarnings: [], rightsWarnings: [], createdAt: AT, updatedAt: AT,
      });
    }
    return out;
  }
}

/** Disabled provider — always valid to select, produces nothing. The safe default. */
export class DisabledCueModelProvider extends DJSEMHuggingFaceCueModelAdapter {
  readonly capabilities: DJSEMCueModelCapabilities = {
    providerId: "disabled", providerType: "disabled", label: "Cue model disabled",
    supportedTaskTypes: [], supportedCueTypes: [], productionEnabled: false,
    warnings: ["AI Cue Director model is disabled. Configure and verify a Hugging Face model to enable suggestions."],
  };
}

/**
 * Manual-stub provider for tests/fixtures ONLY. It echoes a caller-supplied fixture set as raw
 * output so the pipeline can be exercised deterministically without a real model. It never
 * invents suggestions in production (the fixture list is empty unless explicitly provided).
 */
export class ManualStubCueModelProvider extends DJSEMHuggingFaceCueModelAdapter {
  readonly capabilities: DJSEMCueModelCapabilities = {
    providerId: "manual_stub", providerType: "manual_stub", label: "Manual stub cue model (test only)",
    supportedTaskTypes: ["text_classification", "sequence_classification"], supportedCueTypes: [
      "lighting_scene", "lighting_transition", "drop_cue", "build_cue", "breakdown_cue",
      "intensity_ramp", "colour_palette_shift", "lyric_hit", "stem_onset",
    ],
    productionEnabled: false,
    warnings: ["Manual stub provider — for tests/fixtures only, not real inference."],
  };
  constructor(private readonly fixture: Array<Partial<DJSEMAICueSuggestion> & { cueType: string; intent: string }> = []) { super(); }
  override async runInference(_input: unknown): Promise<{ raw: unknown[] }> { return { raw: this.fixture }; }
}

// ---- safety service (non-negotiable boundary) -------------------------------

/** Venue-imposed lighting limits (strobe/blackout allowance, max intensity) used to clamp suggestions. */
export interface DJSEMVenueLimits {
  strobeAllowed?: boolean;
  maxIntensity?: number;      // 0..1 cap
  blackoutAllowed?: boolean;
}

/**
 * Lighting cue safety service. Adds warnings, blocks unsafe suggestions, decides which need
 * explicit approval, and clamps to venue limits. This never certifies safety — it only enforces
 * the software boundary (suggest-only, human review, venue caps, blackout/safe priority).
 */
export class DJSEMLightingCueSafetyService {
  /** Human-readable safety warnings for a suggestion (empty = no concerns). */
  safetyWarningsFor(input: DJSEMAICueSuggestion): string[] {
    const w: string[] = [];
    if (input.cueType === "strobe_suggestion") w.push("Strobe cue — requires venue + operator review; never auto-applied.");
    if (input.cueType === "blackout_suggestion") w.push("Blackout cue — requires manual operator confirmation.");
    if (input.cueType === "freeze_suggestion") w.push("Freeze cue — confirm before use.");
    if (input.confidence < 0.4) w.push("Low-confidence suggestion — explicit acknowledgement required before export.");
    return w;
  }

  /** Structural validity (warnings do NOT make it invalid; only out-of-range values do). */
  validateSuggestion(input: DJSEMAICueSuggestion): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.suggestedIntensity > 1 || input.suggestedIntensity < 0) issues.push({ path: "$.suggestedIntensity", message: "Intensity out of range." });
    if (input.confidence < 0 || input.confidence > 1) issues.push({ path: "$.confidence", message: "Confidence out of range." });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_cue_suggestion");
  }

  /** Hard block: a suggestion that can never be exported (structurally invalid). */
  blockUnsafeSuggestion(input: DJSEMAICueSuggestion): DJSEMValidationResult {
    const issues: ValidationResult["issues"] = [];
    if (input.suggestedIntensity < 0 || input.suggestedIntensity > 1) issues.push({ path: "$.suggestedIntensity", message: "Intensity must be within 0..1." });
    if (input.confidence < 0 || input.confidence > 1) issues.push({ path: "$.confidence", message: "Confidence must be within 0..1." });
    return toDJSEMValidationResult({ valid: issues.length === 0, issues }, "ai_cue_block_check");
  }

  /** Whether a suggestion requires explicit human confirmation before it can be exported. */
  requireApproval(input: DJSEMAICueSuggestion): boolean {
    return SAFETY_SENSITIVE_CUE_TYPES.has(input.cueType) || input.confidence < 0.4;
  }

  /** Clamp a suggestion to venue limits (intensity cap, strobe/blackout allowances). */
  applyVenueLimits(input: DJSEMAICueSuggestion, limits: DJSEMVenueLimits): DJSEMAICueSuggestion {
    const warnings = [...input.safetyWarnings];
    let intensity = input.suggestedIntensity;
    if (typeof limits.maxIntensity === "number" && intensity > limits.maxIntensity) {
      intensity = limits.maxIntensity; warnings.push(`Intensity capped to venue limit ${limits.maxIntensity}.`);
    }
    let approvalStatus = input.approvalStatus;
    if (input.cueType === "strobe_suggestion" && limits.strobeAllowed === false) { warnings.push("Strobe not allowed at this venue — suggestion blocked."); approvalStatus = "rejected"; }
    if (input.cueType === "blackout_suggestion" && limits.blackoutAllowed === false) { warnings.push("Blackout not allowed at this venue — suggestion blocked."); approvalStatus = "rejected"; }
    return { ...input, suggestedIntensity: intensity, safetyWarnings: warnings, approvalStatus };
  }
}

// ---- cue → event mapper -----------------------------------------------------

/** A dry-run lighting event derived from an approved cue; hardwareOutput is always false. */
export interface DJSEMLightingDryRunEvent {
  kind: "lighting_dry_run";
  cueId: string; intent: DJSEMLightingCueIntent; intensity: number; timestampMs: number;
  sceneId?: string; adapterMappingId?: string;
  hardwareOutput: false;      // ALWAYS false — dry-run only
  blocked?: boolean;          // set true when blackout/safe-mode suppresses it
  blockedReason?: string;
}

/** A lightweight semantic-event representation derived from a cue suggestion. */
export interface DJSEMSemanticEventLite {
  kind: "semantic_event";
  cueType: DJSEMAICueSuggestionType; intent: DJSEMLightingCueIntent;
  startTimeMs?: number; chapterId?: string; phraseId?: string; confidence: number;
}

/** A lightweight representation of an approved cue, suitable for export/playback wiring. */
export interface DJSEMApprovedCueLite {
  kind: "approved_cue";
  cueId: string; cueType: DJSEMAICueSuggestionType; intent: DJSEMLightingCueIntent;
  intensity: number; startTimeMs?: number; sceneId?: string; hash?: string;
}

/** Runtime safety gate: when either flag is true, dry-run lighting output is suppressed. */
export interface DJSEMRuntimeGate { blackout: boolean; safeMode: boolean; }

/**
 * Maps approved cue suggestions to events. Real hardware output is NEVER produced — dry-run
 * events carry hardwareOutput:false. Blackout and safe mode suppress dry-run output (recorded
 * as blocked), matching the non-negotiable priority rule.
 */
export class DJSEMCueToLightingEventMapper {
  /**
   * Map an approved suggestion to a dry-run lighting event, suppressed if blackout/safe mode is active.
   * @param input - the cue suggestion to map.
   * @param gate - current blackout/safe-mode state (defaults to both off).
   * @returns a dry-run event (hardwareOutput always false; blocked when gated).
   */
  mapSuggestionToDryRunEvent(input: DJSEMAICueSuggestion, gate: DJSEMRuntimeGate = { blackout: false, safeMode: false }): DJSEMLightingDryRunEvent {
    const blocked = gate.blackout || gate.safeMode;
    return {
      kind: "lighting_dry_run", cueId: input.id, intent: input.intent, intensity: input.suggestedIntensity,
      timestampMs: input.startTimeMs ?? 0, sceneId: input.suggestedSceneId, adapterMappingId: input.suggestedAdapterMappingId,
      hardwareOutput: false,
      ...(blocked ? { blocked: true, blockedReason: gate.blackout ? "blackout active" : "safe mode active" } : {}),
    };
  }

  /**
   * Map a cue suggestion to a lightweight semantic-event representation.
   * @param input - the cue suggestion to map.
   * @returns the semantic-event lite representation.
   */
  mapSuggestionToSemanticEvent(input: DJSEMAICueSuggestion): DJSEMSemanticEventLite {
    return { kind: "semantic_event", cueType: input.cueType, intent: input.intent, startTimeMs: input.startTimeMs, chapterId: input.chapterId, phraseId: input.phraseId, confidence: input.confidence };
  }

  /**
   * Map a cue suggestion to a lightweight approved-cue representation.
   * @param input - the cue suggestion to map.
   * @returns the approved-cue lite representation.
   */
  mapSuggestionToApprovedCue(input: DJSEMAICueSuggestion): DJSEMApprovedCueLite {
    return { kind: "approved_cue", cueId: input.id, cueType: input.cueType, intent: input.intent, intensity: input.suggestedIntensity, startTimeMs: input.startTimeMs, sceneId: input.suggestedSceneId };
  }
}

// ---- suggestion service (request/report/approval/export) --------------------

/** Input for {@link DJSEMAICueSuggestionService.createCueSuggestionRequest}. */
export interface CreateCueSuggestionRequestInput {
  sourceType: DJSEMAICueSuggestionRequest["sourceType"];
  requestedCueTypes: DJSEMAICueSuggestionType[];
  safetyMode: DJSEMAICueSuggestionRequest["safetyMode"];
  trackId?: string; setId?: string; showPackId?: string;
  organisationId?: string; projectId?: string; createdByUserId?: string;
  inputs?: DJSEMAICueSuggestionRequest["inputs"];
}

/**
 * Orchestrates cue-suggestion requests → reports → approval → export. Pure/deterministic; the
 * actual model call is delegated to an adapter. Enforces: approval before export, safety gating,
 * missing-input warnings (never fabricates lyrics/stems), and immutable export summaries.
 */
export class DJSEMAICueSuggestionService {
  constructor(
    private readonly adapter: DJSEMHuggingFaceCueModelAdapter = new DisabledCueModelProvider(),
    private readonly safety: DJSEMLightingCueSafetyService = new DJSEMLightingCueSafetyService(),
  ) {}

  /**
   * Build a new cue-suggestion request from source inputs.
   * @param input - source type, requested cue types, safety mode, and optional inputs.
   * @returns the constructed request.
   */
  createCueSuggestionRequest(input: CreateCueSuggestionRequestInput): DJSEMAICueSuggestionRequest {
    return {
      id: id("cuereq"), organisationId: input.organisationId, projectId: input.projectId,
      trackId: input.trackId, setId: input.setId, showPackId: input.showPackId,
      sourceType: input.sourceType, inputs: input.inputs ?? {}, requestedCueTypes: input.requestedCueTypes,
      safetyMode: input.safetyMode, createdByUserId: input.createdByUserId, createdAt: AT,
    };
  }

  /** Run the (adapter-provided) inference and build a report. Never throws — failures → failed report. */
  async run(request: DJSEMAICueSuggestionRequest, modelId: string, modelRevision?: string): Promise<DJSEMAICueSuggestionReport> {
    const warnings: string[] = [];
    // Honest input warnings — no fabrication.
    if (request.requestedCueTypes.includes("lyric_hit") && !(request.inputs.lyricsText && request.inputs.lyricsText.trim())) warnings.push("Lyric-hit cues requested but no lyrics/transcript provided — none generated.");
    if (request.requestedCueTypes.includes("stem_onset") && !(request.inputs.stemActivity && request.inputs.stemActivity.length)) warnings.push("Stem-onset cues requested but no stem activity provided — none generated.");
    try {
      const prepared = await this.adapter.prepareInput(request);
      const raw = await this.adapter.runInference(prepared);
      let suggestions = await this.adapter.normalizeOutput(raw, request, modelId, modelRevision);
      // Attach safety warnings + drop structurally-unsafe suggestions.
      const rejected: DJSEMAICueSuggestion[] = [];
      suggestions = suggestions.filter((s) => {
        const block = this.safety.blockUnsafeSuggestion(s);
        if (!block.ok) { rejected.push({ ...s, approvalStatus: "rejected", safetyWarnings: block.issues.map((i) => i.message) }); return false; }
        const v = this.safety.validateSuggestion(s);
        if (!v.ok) { rejected.push({ ...s, approvalStatus: "rejected", safetyWarnings: v.issues.map((i) => i.message) }); return false; }
        s.safetyWarnings = this.safety.safetyWarningsFor(s);
        return true;
      });
      const status = warnings.length || suggestions.some((s) => s.safetyWarnings.length) ? "completed_with_warnings" : "completed";
      return {
        id: id("cuerep"), requestId: request.id, status, modelId, modelRevision,
        inputSummary: {
          trackCount: request.trackId ? 1 : undefined,
          chapterCount: request.inputs.chapterTimeline?.length,
          lyricLineCount: request.inputs.lyricsText ? request.inputs.lyricsText.split(/\n+/).filter(Boolean).length : undefined,
          stemCount: request.inputs.stemActivity?.length,
        },
        suggestions, rejectedSuggestions: rejected, warnings, errors: [], createdAt: AT,
      };
    } catch (e) {
      return {
        id: id("cuerep"), requestId: request.id, status: "failed", modelId, modelRevision,
        inputSummary: {}, suggestions: [], rejectedSuggestions: [], warnings,
        errors: [`Cue suggestion inference failed: ${(e as Error).message}`], createdAt: AT,
      };
    }
  }

  /**
   * Approve a cue suggestion, enforcing low-confidence acknowledgement and safety-sensitive confirmation.
   * @param s - the suggestion to approve.
   * @param opts - explicit acknowledgements required for low-confidence / safety-sensitive cues.
   * @returns the approved suggestion, or a validation result describing why approval was blocked.
   */
  approveCueSuggestion(s: DJSEMAICueSuggestion, opts: { acknowledgeLowConfidence?: boolean; confirmSensitive?: boolean } = {}): DJSEMAICueSuggestion | DJSEMValidationResult {
    const block = this.safety.blockUnsafeSuggestion(s);
    if (!block.ok) return block;
    if (this.safety.requireApproval(s)) {
      if (s.confidence < 0.4 && !opts.acknowledgeLowConfidence) return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.confidence", message: "Low-confidence suggestion requires explicit acknowledgement." }] }, "ai_cue_approval");
      if (SAFETY_SENSITIVE_CUE_TYPES.has(s.cueType) && !opts.confirmSensitive) return toDJSEMValidationResult({ valid: false, issues: [{ path: "$.cueType", message: `${s.cueType} requires explicit operator confirmation.` }] }, "ai_cue_approval");
    }
    return { ...s, approvalStatus: "approved", updatedAt: AT };
  }

  /**
   * Reject a cue suggestion.
   * @param s - the suggestion to reject.
   * @returns the suggestion marked rejected.
   */
  rejectCueSuggestion(s: DJSEMAICueSuggestion): DJSEMAICueSuggestion { return { ...s, approvalStatus: "rejected", updatedAt: AT }; }

  /**
   * Apply a manual edit to a cue suggestion's timing/type/intensity fields.
   * @param s - the suggestion to edit.
   * @param patch - the fields to change (intensity is clamped to 0..1).
   * @returns the edited suggestion, marked "edited".
   */
  editCueSuggestion(s: DJSEMAICueSuggestion, patch: Partial<Pick<DJSEMAICueSuggestion, "startTimeMs" | "endTimeMs" | "bar" | "beat" | "cueType" | "intent" | "suggestedIntensity" | "suggestedSceneId">>): DJSEMAICueSuggestion {
    return { ...s, ...patch, suggestedIntensity: patch.suggestedIntensity !== undefined ? clamp01(patch.suggestedIntensity) : s.suggestedIntensity, approvalStatus: "edited", updatedAt: AT };
  }

  /**
   * Export ONLY approved/edited suggestions into an immutable snapshot summary. Rejected and
   * still-suggested cues are excluded and listed. Returns the included set + an exclusion report.
   */
  exportApprovedSuggestionsToSnapshot(suggestions: DJSEMAICueSuggestion[], hash: (payload: unknown) => string): {
    exported: DJSEMAICueSuggestion[]; excluded: Array<{ id: string; reason: string }>; hash: string; count: number;
  } {
    const exported: DJSEMAICueSuggestion[] = []; const excluded: Array<{ id: string; reason: string }> = [];
    for (const s of suggestions) {
      if (s.approvalStatus === "approved" || s.approvalStatus === "edited") exported.push({ ...s, approvalStatus: "exported", updatedAt: AT });
      else excluded.push({ id: s.id, reason: `not exportable in status '${s.approvalStatus}' (only approved/edited export)` });
    }
    return { exported, excluded, hash: hash({ exported }), count: exported.length };
  }

  /** Batch approve safe, high-confidence, non-sensitive suggestions; return approved + skipped. */
  batchApprove(suggestions: DJSEMAICueSuggestion[], minConfidence: number): { approved: DJSEMAICueSuggestion[]; skipped: Array<{ id: string; reason: string }> } {
    const approved: DJSEMAICueSuggestion[] = []; const skipped: Array<{ id: string; reason: string }> = [];
    for (const s of suggestions) {
      if (SAFETY_SENSITIVE_CUE_TYPES.has(s.cueType)) { skipped.push({ id: s.id, reason: "safety-sensitive cue excluded from batch approve" }); continue; }
      if (s.confidence < minConfidence) { skipped.push({ id: s.id, reason: `confidence ${s.confidence} below threshold ${minConfidence}` }); continue; }
      const r = this.approveCueSuggestion(s);
      if ("approvalStatus" in r) approved.push(r); else skipped.push({ id: s.id, reason: "blocked by safety" });
    }
    return { approved, skipped };
  }
}

// ---- provider registry (fails closed to disabled) ---------------------------

/** Registry of cue model providers, keyed by provider id; fails closed to the disabled provider. */
export class DJSEMCueModelProviderRegistry {
  private readonly providers = new Map<string, DJSEMHuggingFaceCueModelAdapter & { capabilities?: DJSEMCueModelCapabilities }>();
  private readonly fallback = new DisabledCueModelProvider();
  constructor() { this.providers.set("disabled", this.fallback); }
  /**
   * Register (or replace) a cue model provider by id.
   * @param providerId - the id to register the provider under.
   * @param provider - the provider instance.
   */
  register(providerId: string, provider: DJSEMHuggingFaceCueModelAdapter & { capabilities?: DJSEMCueModelCapabilities }): void { this.providers.set(providerId, provider); }
  /**
   * Look up a registered cue model provider by id.
   * @param providerId - the provider id.
   * @returns the provider, or the disabled fallback if not registered.
   */
  getProvider(providerId: string): DJSEMHuggingFaceCueModelAdapter { return this.providers.get(providerId) ?? this.fallback; }
  /** Never throws — unknown/absent → disabled provider (no suggestions). */
  selectProvider(providerId: string | undefined): DJSEMHuggingFaceCueModelAdapter { return providerId ? this.getProvider(providerId) : this.fallback; }
}

// ---- helpers ----------------------------------------------------------------
function clamp01(x: number): number { return x < 0 ? 0 : x > 1 ? 1 : x; }
